import type {
  BulkDeleteRequestsBody,
  BulkDeleteRequestsResponse,
} from "@nlc/shared-types";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "@/components/toast/toast-queue";
import { requestListKeys } from "@/hooks/queryKeys";
import apiClient from "@/lib/api/client";

interface UseBulkDeleteRequestsOptions {
  onError?: (error: Error) => void;
  onSuccess?: (data: BulkDeleteRequestsResponse) => void;
}

export function useBulkDeleteRequests(
  options: UseBulkDeleteRequestsOptions = {},
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestIds: number[]) => {
      const body: BulkDeleteRequestsBody = {
        requestIds,
      };

      return await apiClient
        .url("/requests/list/bulk-delete")
        .post(body)
        .json<BulkDeleteRequestsResponse>();
    },
    onError: (error: Error) => {
      toast.add({
        description: "Failed to delete requests. Please try again.",
        title: "Error",
        type: "error",
      });
      options.onError?.(error);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: requestListKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: ["designerRequestCounts"],
      });
      queryClient.invalidateQueries({
        queryKey: ["teamLeadCounts"],
      });
      queryClient.invalidateQueries({
        queryKey: ["request-list", "superadmin-counts"],
      });
      queryClient.invalidateQueries({
        queryKey: ["customerSuccessCounts"],
      });

      options.onSuccess?.(data);
    },
  });
}
